import type { CostEstimate } from '@cartwheel/api/v1'
import { PerSessionSummary } from './per-session-summary'

interface CommercialEstimateDisplayProps {
  estimate: CostEstimate
}

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between py-2 px-4 border-b border-gray-stroke font-medium">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  )
}

function deductibleLevel(editable: CostEstimate['editable']): string | null {
  const hasIndividual = Number(editable.individual_deductible ?? 0) > 0
  const hasFamily = Number(editable.family_deductible ?? 0) > 0
  if (hasIndividual && hasFamily) return 'Individual + Family'
  if (hasIndividual) return 'Individual'
  if (hasFamily) return 'Family'
  return null
}

function formatCurrency(amount: string | null): string {
  if (amount === null || amount === '') return '$0'
  const n = Number(amount)
  if (Number.isNaN(n) || n === 0) return '$0'
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

export function CommercialEstimateDisplay({ estimate }: CommercialEstimateDisplayProps) {
  const { editable, costs, deductible, post_deductible_cost: postDeductible } = estimate

  const hasDeductible = deductibleLevel(editable) !== null
  const isMet = deductible?.is_met ?? false

  const deductibleStatus = hasDeductible ? (isMet ? 'Met' : 'Yes') : 'No'

  // For commercial display we mirror the ERB partial field-for-field.
  // costs.ongoing_session / costs.first_appointment swap meaning between the
  // not-met and met-with-coinsurance cases — see ClientCostEstimateDecorator —
  // so we derive the per-session summary from those rather than re-implementing
  // the rate math client-side.
  const ongoing = costs.ongoing_session
  const intake = costs.first_appointment
  const range = ongoing === intake ? ongoing : `${ongoing}-${intake}`

  return (
    <>
      <div className="my-4">
        <div className="flex justify-between py-2 px-4 border-t border-b border-gray-stroke font-medium">
          <span>Deductible</span>
          <span>{deductibleStatus}</span>
        </div>

        {hasDeductible && !isMet && (
          <>
            <DetailRow label="Level" value={deductibleLevel(editable) ?? ''} />
            {Number(editable.individual_deductible ?? 0) > 0 && (
              <>
                <DetailRow
                  label="Individual Deductible"
                  value={formatCurrency(editable.individual_deductible)}
                />
                {editable.amount_remaining_individual_deductible !== null && (
                  <DetailRow
                    label="Individual Deductible Remaining"
                    value={formatCurrency(editable.amount_remaining_individual_deductible)}
                  />
                )}
              </>
            )}
            {Number(editable.family_deductible ?? 0) > 0 && (
              <>
                <DetailRow
                  label="Family Deductible"
                  value={formatCurrency(editable.family_deductible)}
                />
                {editable.amount_remaining_family_deductible !== null && (
                  <DetailRow
                    label="Family Deductible Remaining"
                    value={formatCurrency(editable.amount_remaining_family_deductible)}
                  />
                )}
              </>
            )}
          </>
        )}

        {editable.coinsurance_estimate !== null && editable.coinsurance_estimate > 0 && (
          <DetailRow label="Coinsurance" value={`${editable.coinsurance_estimate}%`} />
        )}

        {editable.copay !== null && (
          <DetailRow label="Copay" value={formatCurrency(editable.copay)} />
        )}

        {editable.session_limit !== null && (
          <DetailRow label="Session Limits" value={String(editable.session_limit)} />
        )}
      </div>

      <div className="flex justify-center">
        {isMet ? (
          <PerSessionSummary label="Deductible Met" labelColor="mint-50" perSessionAmount={range} />
        ) : hasDeductible ? (
          <>
            <div className="border-r border-gray-stroke pr-8">
              <PerSessionSummary
                label="Before Deductible Met"
                labelColor="orange-25"
                perSessionAmount={range}
              />
            </div>
            <div className="pl-8">
              <PerSessionSummary
                label="After Deductible Met"
                labelColor="mint-50"
                perSessionAmount={postDeductible.amount}
              />
            </div>
          </>
        ) : editable.copay !== null ? (
          <PerSessionSummary
            label="Copay only"
            labelColor="sand-dark"
            perSessionAmount={formatCurrency(editable.copay)}
          />
        ) : null}
      </div>

      {estimate.custom_message && <CustomMessageBlock message={estimate.custom_message} />}
    </>
  )
}

export function CustomMessageBlock({ message }: { message: string }) {
  return (
    <div className="grid gap-2 mt-4">
      <h5 className="font-ginto-nord-md text-base font-semibold text-charcoal">Custom message</h5>
      <p className="text-charcoal-50 text-sm bg-white-dark border border-gray-stroke rounded-lg py-2 px-4">
        {message}
      </p>
      <p className="text-charcoal-50 text-sm">Displayed on cost estimate page</p>
    </div>
  )
}
